"use client";

import { useEffect, useRef, useState } from "react";
import { useLocale } from "next-intl";
import { MdDone, MdOutlineKeyboardArrowDown, MdPublic } from "react-icons/md";
import { CircleFlagLanguage } from "react-circle-flags";
import { localesData } from "@/lib/locale-data/locale";
import { resolveLanguageSelectorKey } from "./language-selector-keyboard";
import styles from "./language-selector.module.css";

const uniqueLocales = localesData
  .filter(
    (item, index, items) =>
      index === items.findIndex((candidate) => candidate.locale === item.locale),
  )
  .sort((left, right) => left.name.localeCompare(right.name));

export default function ComingSoonLanguageSelector() {
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const currentLocale = useLocale();
  const dropdownRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const optionRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const selectedIndex = Math.max(
    0,
    uniqueLocales.findIndex((item) => item.locale === currentLocale),
  );

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelect = (newLocale: string) => {
    const target = uniqueLocales.find((item) => item.locale === newLocale);
    setIsOpen(false);

    if (target) {
      const baseUrl =
        process.env.NODE_ENV === "development"
          ? `http://${target.domain}:3000`
          : `https://${target.domain}`;

      window.location.assign(`${baseUrl}${target.visiblePrefix || "/"}`);
    }
  };

  const openAndFocus = (index = selectedIndex) => {
    setActiveIndex(index);
    setIsOpen(true);
    window.requestAnimationFrame(() => optionRefs.current[index]?.focus());
  };

  const handleTriggerKeyDown = (
    event: React.KeyboardEvent<HTMLButtonElement>,
  ) => {
    const result = resolveLanguageSelectorKey({
      key: event.key,
      source: "trigger",
      isOpen,
      activeIndex,
      selectedIndex,
      optionCount: uniqueLocales.length,
    });

    if (!result.handled) return;
    event.preventDefault();
    if (result.open === false) setIsOpen(false);
    if (result.focusTrigger) triggerRef.current?.focus();
    if (result.open && result.activeIndex !== undefined) {
      openAndFocus(result.activeIndex);
    }
  };

  const handleOptionKeyDown = (
    event: React.KeyboardEvent<HTMLButtonElement>,
    index: number,
  ) => {
    const result = resolveLanguageSelectorKey({
      key: event.key,
      source: "option",
      isOpen,
      activeIndex: index,
      selectedIndex,
      optionCount: uniqueLocales.length,
    });

    if (!result.handled) return;
    event.preventDefault();
    if (result.activate) {
      handleSelect(uniqueLocales[index].locale);
      triggerRef.current?.focus();
      return;
    }
    if (result.open === false) setIsOpen(false);
    if (result.focusTrigger) triggerRef.current?.focus();
    if (result.activeIndex !== undefined) {
      setActiveIndex(result.activeIndex);
      optionRefs.current[result.activeIndex]?.focus();
    }
  };

  return (
    <div className={styles.wrapper} ref={dropdownRef} data-nosnippet>
      <button
        ref={triggerRef}
        className={styles.triggerBtn}
        onClick={() => {
          setActiveIndex(selectedIndex);
          setIsOpen(!isOpen);
        }}
        onKeyDown={handleTriggerKeyDown}
        type="button"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-controls="coming-soon-language-options"
      >
        <MdPublic className={styles.iconSm} />
        <span className={styles.triggerText}>{currentLocale.toUpperCase()}</span>
        <MdOutlineKeyboardArrowDown
          className={`${styles.iconSm} ${isOpen ? styles.openChevron : ""}`}
        />
      </button>

      {isOpen && (
        <div className={styles.dropdown}>
          <ul
            className={styles.list}
            id="coming-soon-language-options"
            role="listbox"
          >
            {uniqueLocales.map((item, index) => {
              const isActive = item.locale === currentLocale;

              return (
                <li
                  key={item.locale}
                  className={`${styles.item} ${isActive ? styles.activeItem : ""}`}
                  role="presentation"
                >
                  <button
                    type="button"
                    role="option"
                    aria-selected={isActive}
                    tabIndex={index === activeIndex ? 0 : -1}
                    ref={(element) => {
                      optionRefs.current[index] = element;
                    }}
                    onClick={() => handleSelect(item.locale)}
                    onFocus={() => setActiveIndex(index)}
                    onKeyDown={(event) => handleOptionKeyDown(event, index)}
                    className={styles.itemLeft}
                  >
                    <span className={styles.flagWrapper}>
                      <CircleFlagLanguage
                        languageCode={item.locale}
                        className={styles.flag}
                      />
                    </span>
                    <span className={styles.nameText} lang={item.locale}>
                      {item.name}
                    </span>
                  </button>
                  {isActive && <MdDone className={styles.checkIcon} />}
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}
